import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Icon from '../../../../components/AppIcon';
import { getClockingsPerDay, editClockTime } from '../../../../api/Attendance/attendance';
import { ClientType } from '../../../../models/general';
import { getFullUrl } from '../../../../utils/url';

const toDate = (v) => {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d;
};

const toDatetimeLocalValue = (d) => {
  const dt = toDate(d);
  if (!dt) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};

const formatClockingType = (t, tr) => {
  const raw = String(t);
  switch (raw) {
    case '0':
    case 'ClockIn':
      return tr('Clock In');
    case '1':
    case 'ClockOut':
      return tr('Clock Out');
    case '2':
    case 'BreakIn':
      return tr('Break In');
    case '3':
    case 'BreakOut':
      return tr('Break Out');
    default:
      return raw;
  }
};

const normalizeId = (v) => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

const clockingRowKey = (c) => {
  // Shift clock-in/out share the same shift id, so key by (type + id) not by id alone.
  const id = normalizeId(c?.id);
  if (id == null) return null;
  return `${String(c?.type)}:${id}`;
};

const DayRecordDetailsModal = ({
  open,
  record,
  canEdit,
  onClose,
  onUpdated,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clockings, setClockings] = useState([]);

  // Store per-clocking edit values keyed by clocking id to avoid shared state across rows.
  const [editingValuesById, setEditingValuesById] = useState({});
  const [saving, setSaving] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const dateKey = record?.dateKey;

  const headerDate = useMemo(() => {
    if (!dateKey) return null;
    return new Date(`${dateKey}T00:00:00`);
  }, [dateKey]);

  const loadClockings = async () => {
    if (!record?.userId || !dateKey) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getClockingsPerDay(record.userId, dateKey, dateKey);
      if (res?.status !== 200) {
        throw new Error('Failed to load day clockings');
      }
      const day = Array.isArray(res?.payload) ? res.payload?.[0] : [];
      const sorted = (day || []).slice().sort((a, b) => {
        const ta = toDate(a?.time)?.getTime() ?? 0;
        const tb = toDate(b?.time)?.getTime() ?? 0;
        return ta - tb;
      });
      setClockings(sorted);
    } catch (e) {
      console.error('Failed to load day details', e);
      setError(t('Failed to load day details.'));
      setClockings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setEditingValuesById({});
    loadClockings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, record?.userId, dateKey]);

  if (!open) return null;

  const LoadingClockingsShimmer = () => (
    <div className="space-y-2" aria-busy="true" aria-live="polite">
      {[0, 1, 2, 3, 4].map((i) => (
        <div key={i} className="border border-border rounded-lg p-3 flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="h-4 w-[55%] bg-muted animate-pulse rounded" />
            <div className="h-4 w-[35%] bg-muted animate-pulse rounded mt-1" />
          </div>
          <div className="w-8 h-8 bg-muted animate-pulse rounded" />
        </div>
      ))}
    </div>
  );

  const startEdit = (c) => {
    const key = clockingRowKey(c);
    if (!key) return;
    setEditingValuesById((prev) => ({
      ...prev,
      [key]: toDatetimeLocalValue(c?.time),
    }));
  };

  const cancelEdit = (c) => {
    const key = clockingRowKey(c);
    if (!key) return;
    setEditingValuesById((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const saveEdit = async (c) => {
    if (!canEdit) return;
    if (!record?.userId) return;

    const id = normalizeId(c?.id);
    const key = clockingRowKey(c);
    if (id == null || !key) return;
    const editingValue = editingValuesById?.[key];
    const d = editingValue ? new Date(editingValue) : null;
    if (!d || Number.isNaN(d.getTime())) {
      alert(t('Please enter a valid time.'));
      return;
    }

    try {
      setSaving(true);
      const res = await editClockTime(id, {
        userId: record.userId,
        type: c.type,
        time: d.toISOString(),
        clientType: ClientType.Web,
      });

      if (res?.status !== 200) {
        throw new Error('Edit clocking failed');
      }

      cancelEdit(c);
      await loadClockings();
      onUpdated?.();
    } catch (e) {
      console.error('Failed to edit clock time', e);
      alert(t('Failed to update clock time. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card border border-border rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-semibold text-foreground">{t('Day Details')}</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {record?.employee}{' '}
              {headerDate ? (
                <span>
                  • {headerDate.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                </span>
              ) : null}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {loading ? (
          <div className="mt-4">
            <div className="text-sm font-medium text-foreground mb-2">{t('Clockings')}</div>
            <LoadingClockingsShimmer />
          </div>
        ) : null}
        {error ? (
          <div className="mt-3 bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm">
            {error}
          </div>
        ) : null}

        {!loading ? (
        <div className="mt-4">
          <div className="text-sm font-medium text-foreground mb-2">{t('Clockings')}</div>

          {(!clockings || clockings.length === 0) && !loading ? (
            <div className="text-sm text-muted-foreground">{t('No clockings found for this day.')}</div>
          ) : null}

          <div className="space-y-2">
            {(clockings || []).map((c) => {
              const id = normalizeId(c?.id);
              const rowKey = clockingRowKey(c);
              const isEditing = Boolean(rowKey) && Object.prototype.hasOwnProperty.call(editingValuesById || {}, rowKey);
              const editingValue = rowKey ? (editingValuesById?.[rowKey] || '') : '';
              const timeLabel = toDate(c?.time)?.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) || '-';
              return (
                <div key={rowKey || `${String(c?.type)}:${String(c?.id)}`} className="border border-border rounded-lg p-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground">{formatClockingType(c?.type, t)}</span>
                      <span className="text-sm text-muted-foreground">•</span>
                      <span className="text-sm text-foreground">{timeLabel}</span>
                      {c?.isEdited ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">{t('Edited')}</span>
                      ) : null}
                    </div>
                    {c?.comments ? (
                      <div className="text-xs text-muted-foreground mt-1 truncate" title={c?.comments}>
                        {c?.comments}
                      </div>
                    ) : null}
                  </div>

                  <div className="flex items-center gap-2">
                    {c?.imageUrl ? (
                      <button
                        type="button"
                        onClick={() => setPreviewImage(getFullUrl(c.imageUrl))}
                        className="flex-shrink-0 rounded-lg overflow-hidden hover:ring-2 hover:ring-primary/50 transition-all"
                        title={t('View photo')}
                      >
                        <img
                          src={getFullUrl(c.imageUrl)}
                          alt=""
                          className="h-10 w-10 rounded-lg object-cover"
                        />
                      </button>
                    ) : null}

                  {canEdit ? (
                    <div className="flex items-center gap-2">
                      {isEditing ? (
                        <>
                          <input
                            type="datetime-local"
                            value={editingValue}
                            onChange={(e) => {
                              const nextVal = e?.target?.value;
                              if (!rowKey) return;
                              setEditingValuesById((prev) => ({ ...prev, [rowKey]: nextVal }));
                            }}
                            className="px-2 py-1 text-sm border border-border rounded bg-background text-foreground"
                          />
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => saveEdit(c)}
                            className="px-3 py-1 text-sm bg-primary text-primary-foreground rounded hover:bg-primary/90 disabled:opacity-50"
                          >
                            {t('Save')}
                          </button>
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => cancelEdit(c)}
                            className="px-3 py-1 text-sm bg-muted text-foreground rounded hover:bg-muted/80 disabled:opacity-50"
                          >
                            {t('Cancel')}
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => startEdit(c)}
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                          title={t('Edit')}
                          disabled={id == null || !rowKey}
                        >
                          <Icon name="Pencil" size={16} />
                        </button>
                      )}
                    </div>
                  ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        ) : null}

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-colors"
          >
            {t('Close')}
          </button>
        </div>
      </div>

      {previewImage ? (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60]"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-lg max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setPreviewImage(null)}
              className="absolute -top-3 -right-3 bg-white rounded-full p-1 shadow-lg hover:bg-gray-100 z-10"
            >
              <Icon name="X" size={18} />
            </button>
            <img
              src={previewImage}
              alt={t('Clocking photo')}
              className="max-w-full max-h-[80vh] rounded-lg object-contain"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DayRecordDetailsModal;
